// src/pages/SuccessPage.jsx
import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { buildTicketDataUrl } from "../utils/ticket";

const SuccessPage = () => {
  const location = useLocation();
  const { qrData } = location.state || {};
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);

  // Generează și descarcă biletul brandat cu QR de înaltă rezoluție.
  const downloadTicket = async () => {
    if (!qrData || downloading) return;
    setDownloading(true);
    try {
      const url = await buildTicketDataUrl(qrData, {
        eventName: t("event_name"),
        invitationPass: t("invitation_pass"),
        dateLabel: t("date_label"),
        eventDate: t("event_date"),
        locationLabel: t("location_label"),
        eventLocation: t("event_location"),
        ticketPresent: t("ticket_present"),
      });
      const a = document.createElement("a");
      a.href = url;
      a.download = "invitatie-expoapa-2026.png";
      a.click();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-ara-gradient min-h-screen flex items-center justify-center px-4 py-12">
      <div className="sp-card w-full max-w-lg rounded-[28px] border border-white/60 bg-white/90 backdrop-blur-sm shadow-2xl shadow-[#0066A6]/10 p-8 sm:p-10 text-center">

        {/* Badge de succes animat */}
        <div className="relative mx-auto mb-6 h-20 w-20">
          <span className="sp-badge-ring absolute inset-0 rounded-full bg-[#22C55E]/30" />
          <div
            className="sp-badge relative flex h-20 w-20 items-center justify-center rounded-full shadow-lg shadow-[#22C55E]/30"
            style={{ background: "linear-gradient(135deg,#22C55E,#0066A6)" }}
          >
            <svg className="h-10 w-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path className="sp-check-path" d="M5 13l4 4 10-11" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold text-[#003B5C] mb-2" style={{ fontFamily: '"Aeonik", Arial, sans-serif' }}>
          {t("registration_success")}
        </h2>
        <p className="text-[15px] text-gray-600 mb-8" style={{ fontFamily: '"Aeonik", Arial, sans-serif' }}>
          {t("thank_you_registration")}
        </p>

        {/* Detalii eveniment */}
        <div className="mb-8 grid gap-3 text-left">
          <div className="flex items-center gap-3 rounded-2xl bg-[#F1F8FF] px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0066A6]/10 text-lg">📅</span>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#0066A6] font-semibold">{t("date_label")}</p>
              <p className="text-sm font-medium text-[#003B5C]">{t("event_date")}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl bg-[#F1F8FF] px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0066A6]/10 text-lg">📍</span>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#0066A6] font-semibold">{t("location_label")}</p>
              <p className="text-sm font-medium text-[#003B5C]">{t("event_location")}</p>
            </div>
          </div>
        </div>

        {/* QR Code */}
        {qrData ? (
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: '"Aeonik", Arial, sans-serif' }}>
              {t("invitation_email")}
            </p>
            <div className="mx-auto inline-flex flex-col items-center">
              <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-lg shadow-[#0066A6]/5">
                <QRCodeCanvas value={qrData} size={190} level="H" fgColor="#0B2A3C" bgColor="#ffffff" />
              </div>
              <button
                onClick={downloadTicket}
                disabled={downloading}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0066A6] px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-[#0066A6]/25 transition-all hover:bg-[#005377] hover:shadow-lg active:scale-95 disabled:opacity-70"
              >
                {downloading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M10 3v10m0 0l-4-4m4 4l4-4M4 17h12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {t("download_qr")}
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-6 flex items-center justify-center gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
            <svg className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="10" cy="10" r="7.5" />
              <path d="M10 6.5v4M10 13.5h.01" strokeLinecap="round" />
            </svg>
            {t("missing_qr")}
          </div>
        )}

        {/* Buton înapoi */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0066A6] transition hover:text-[#003B5C]"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 5l-5 5 5 5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t("back_home")}
        </Link>

        {/* Contact */}
        <p className="mt-6 border-t border-gray-100 pt-5 text-xs text-gray-500" style={{ fontFamily: '"Aeonik", Arial, sans-serif' }}>
          {t("contact_us")}{" "}
          <a href="mailto:office@ara.ro" className="font-medium text-[#0066A6] underline">office@ara.ro</a>
        </p>
      </div>
    </div>
  );
};

export default SuccessPage;
